import { useMutation } from "@apollo/client";
import { InfoModal } from "@starrez/starryui";
import { FormikProps } from "formik";
import { decode } from "html-entities";
import * as React from "react";
import Editor, { FileInfo } from "react-email-editor";
import { IUnlayerOptions } from "../../../../../typings/unlayer";
import { ButtonBarButton, ButtonBarCenter } from "../../../../components/ButtonBar/ButtonBar.styles";
import { Button } from "../../../../components/Buttons/Button";
import { AdvancedEmailWizardContext } from "../../../../context";
import { strings } from "../../../../localisation";
import { ISendEmailDataModel } from "../../../EmailWizard/models/SendEmail";
import { ITemplateDataModel } from "../../../TemplateWizard/models/Template";
import { ExecuteModel } from "../../ExecuteModel";
import { IUploadImageResult, uploadImageMutation } from "./graphQL";

export interface IEmailEditorProps {
    FormikProps: FormikProps<ExecuteModel<ITemplateDataModel | ISendEmailDataModel>> | undefined;
    OnLoad: () => void;
    IsShown: boolean;
}

export const EmailEditor = (props: IEmailEditorProps) => {
    // Context
    const { MergeTagData } = React.useContext(AdvancedEmailWizardContext);

    // State
    // There is a known issue in Unlayer where it's not guaranteed that the editor is loaded before the API is accessed https://github.com/unlayer/react-email-editor/issues/22
    // We need to do the following as a workaround as suggested here https://github.com/unlayer/react-email-editor/issues/22#issuecomment-482104878
    const [isEditorLoaded, setIsEditorLoaded] = React.useState(false);
    const [isComponentMounted, setIsComponentMounted] = React.useState(false);
    const [imageUploadError, setImageUploadError] = React.useState<string>();

    const emailEditorRef = React.useRef() as any;
    const unlayerOptions: IUnlayerOptions = {
        features: {
            stockImages: false,
            textEditor: {
                spellChecker: true,
                tables: true,
            },
        },
        mergeTagsConfig: {
            sort: false,
        },
        tools: {
            menu: {
                enabled: false,
            },
        },
        locale: "en-US",
        translations: {
            "en-US": {
                "labels.merge_tags": "Merge Fields",
            },
        },
    };

    React.useEffect(() => setIsComponentMounted(true), []);

    // Attempt to load merge tags if editor is loaded
    React.useEffect(() => loadMergeTags(), [isEditorLoaded]);

    // Attempt to load merge tags if component is mount
    React.useEffect(() => loadMergeTags(), [isComponentMounted]);

    React.useEffect(() => {
        if (isEditorLoaded) {
            loadTemplate();

            // Add event handlers
            emailEditorRef.current.editor.addEventListener("design:loaded", handleDesignLoaded);
            emailEditorRef.current.editor.addEventListener("design:updated", handleDesignUpdated);

            emailEditorRef.current.registerCallback("image", (files: FileInfo, done: any) => {
                handleImageUpload(files, done);
            });
        }
    }, [isEditorLoaded]);

    React.useEffect(() => {
        if (isEditorLoaded && !props.IsShown) {
            loadTemplate();
        }
    }, [props.IsShown, props.FormikProps?.values.formDataModel.UnlayerTemplate]);

    const isTemplatePopulated = (unlayerTemplate: {} | undefined) => {
        return unlayerTemplate && (JSON.stringify(unlayerTemplate) !== JSON.stringify({}));
    };

    // Load methods
    const loadTemplate = () => {
        if (props.FormikProps && isTemplatePopulated(props.FormikProps.values.formDataModel.UnlayerTemplate)) {
            emailEditorRef.current.editor.loadDesign(props.FormikProps.values.formDataModel.UnlayerTemplate);
        } else {
            emailEditorRef.current.editor.loadBlank();
        }
    };

    const loadMergeTags = () => {
        // Return if component is not mounted or if the editor is loaded yet
        if (!isComponentMounted || !isEditorLoaded) {
            return;
        }

        // Set tags when the component is mounted or the editor is loaded
        emailEditorRef.current.setMergeTags(MergeTagData?.mergeTags);
    };

    // Event Handlers
    const handleDesignLoaded = () => {
        props.OnLoad();
    };

    const handleDesignUpdated = () => {
        emailEditorRef.current.editor.exportHtml((data: any) => { // TODO: Create type for this as there isn't one out the box?
            const { design, html } = data;
            if (props.FormikProps) {
                props.FormikProps.setFieldValue("formDataModel.UnlayerTemplate", design);
                props.FormikProps.setFieldValue("formDataModel.EmailBody", decode(html, { level: 'html5' }));
            }
        });
    };

    const handleImageUpload = (files: FileInfo, done: (obj: object) => void) => {
        const fileReader = new FileReader();
        if (files.attachments && files.attachments.length) {
            fileReader.readAsDataURL(files.attachments[0]);
            fileReader.onload = () => {
                uploadImage({
                    variables: {
                        uploadImageDetails: {
                            name: files.attachments[0].name,
                            image: fileReader.result,
                        },
                    },
                }).then((result) => {
                    if (result.data?.communication?.uploadEmailImage?.url) {
                        done({ progress: 100, url: result.data?.communication?.uploadEmailImage?.url });
                    } else {
                        setImageUploadError(strings.Message_Error_ImageUpload);
                    }
                });
            };
        }
    };

    const [uploadImage] = useMutation<IUploadImageResult>(uploadImageMutation, {
        onError: () => {
            setImageUploadError(strings.Message_Error_ImageUpload);
        },
    });

    const onEditorLoad = () => {
        setIsEditorLoaded(true);
    };

    return (
        <>
            {imageUploadError && imageUploadError.length > 0 &&
                <InfoModal
                    InfoType="Error"
                    CloseAriaLabel={strings.Label_Error_ClosePopup}
                    Header={strings.Message_Error_ImageUploadHeader}
                    ListenToBackgroundClick={false}
                    OnCloseRequest={() => setImageUploadError("")}
                    RenderFooter={() => (
                        <ButtonBarCenter>
                            <ButtonBarButton>
                                <Button
                                    variant={"Primary"}
                                    onClick={() => {
                                        setImageUploadError("");
                                    }}>
                                    {strings.Action_OK}
                                </Button>
                            </ButtonBarButton>
                        </ButtonBarCenter>
                    )}
                    ContentMessage={imageUploadError}
                />
            }

            <Editor
                projectId={3152}
                ref={emailEditorRef}
                onLoad={onEditorLoad}
                options={unlayerOptions}
                minHeight={100}
            />
        </>
    );
};
